import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Package, X } from "lucide-react";
import { Button } from "@/design-system/primitives";
import { track } from "@/lib/analytics";
import type { SupplementProduct } from "./api";
import {
  SUPPLEMENT_ANALYTICS_CONTEXT,
  supplementProductProperties,
} from "./analytics";
import { isPurchasableSupplement, supplementPrice } from "./catalog";
import { cart, useCart } from "../stores/cart";
import { cartUi } from "../stores/cartUi";

export function SupplementImage({
  product,
  className = "",
}: {
  product: SupplementProduct;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  return product.imageUrl && !failed ? (
    <img
      src={product.imageUrl}
      alt={product.title}
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
      className={`object-contain ${className}`}
    />
  ) : (
    <div
      className={`flex flex-col items-center justify-center gap-10 bg-raised text-ink-secondary ${className}`}
    >
      <Package size={32} strokeWidth={1.2} aria-hidden />
      <span className="type-label-xs">Image unavailable</span>
    </div>
  );
}

export function SupplementDetail({
  product,
  checkoutEnabled,
  onAdded,
  onReturnFocus,
}: {
  product: SupplementProduct;
  checkoutEnabled: boolean;
  onAdded: () => void;
  onReturnFocus: () => void;
}) {
  const [quantity, setQuantity] = useState(1);
  const inCart = useCart((state) =>
    state.items.some((item) => item.id === product.id),
  );
  const cartFull = useCart(
    (state) =>
      state.items.filter((item) => item.origin === "supplement").length >= 10,
  );
  const canBuy = checkoutEnabled && isPurchasableSupplement(product);

  function addToCart() {
    if (!canBuy || product.priceCents == null) return;
    if (inCart || cartFull) {
      onAdded();
      cartUi.open();
      return;
    }
    const added = cart.add(
      {
        id: product.id,
        slug: product.slug,
        title: product.title,
        subtitle: [product.brand, product.size].filter(Boolean).join(" · "),
        price_cents: product.priceCents,
        image_url: product.imageUrl,
        origin: "supplement",
        supplement_category: product.category,
        supplement_brand: product.brand,
        supplement_asin: product.asin,
      },
      quantity,
    );
    if (added)
      track("supplement_added_to_cart", {
        ...SUPPLEMENT_ANALYTICS_CONTEXT,
        ...supplementProductProperties(product),
        quantity,
        amount_cents: product.priceCents * quantity,
      });
    onAdded();
    cartUi.open();
  }

  return (
    <Dialog.Portal>
      <Dialog.Overlay className="fixed inset-0 z-dialog bg-[var(--surface-overlay)]" />
      <Dialog.Content
        className="fixed z-dialog bottom-0 right-0 w-full md:w-[52rem] h-[calc(100dvh-4rem)] md:h-dvh bg-page border-l border-line-subtle flex flex-col focus-visible:outline-none"
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          onReturnFocus();
        }}
      >
        <div className="flex items-center justify-between border-b border-line-subtle pl-20 pr-5 shrink-0">
          <span className="type-label-sm">Supplement details</span>
          <Dialog.Close asChild>
            <button
              type="button"
              className="w-44 h-44 shrink-0 flex items-center justify-center hover:bg-raised focus-visible:shadow-focus"
              aria-label="Close supplement details"
            >
              <X size={18} aria-hidden />
            </button>
          </Dialog.Close>
        </div>
        <div className="min-h-0 overflow-y-auto overscroll-contain flex-1 p-20 md:p-30">
          <SupplementImage
            key={product.imageUrl}
            product={product}
            className="h-[24rem] w-full p-20 mb-25"
          />
          <p className="type-label-sm text-ink-secondary mb-10">
            {product.brand}
          </p>
          <Dialog.Title className="font-display text-24 leading-110">
            {product.title}
          </Dialog.Title>
          <p className="type-body-sm text-ink-secondary mt-10">
            {[product.size, product.form].filter(Boolean).join(" · ")}
          </p>
          <Dialog.Description className="type-body-md text-ink-secondary mt-15 leading-140">
            {product.description || product.shortDescription}
          </Dialog.Description>
          <div className="py-20 my-20 border-y border-line-subtle">
            <p className="font-mono text-24 tabular-nums">
              {product.priceCents == null &&
              product.priceStatus === "refreshing"
                ? "Checking price"
                : supplementPrice(product.priceCents)}
            </p>
            <p className="type-body-sm mt-8 text-ink-secondary">
              Shipping and tax included.
            </p>
            <p className="type-label-xs text-ink-secondary mt-10">
              One-time purchase
            </p>
          </div>
          {product.qualityNotes.length > 0 && (
            <section className="mb-20">
              <h3 className="type-label-sm mb-10">Product quality</h3>
              <ul className="list-disc pl-20 space-y-8 type-body-sm text-ink-secondary leading-140">
                {product.qualityNotes.map((note) => (
                  <li key={note}>{note}</li>
                ))}
              </ul>
            </section>
          )}
          {product.servingInfo && (
            <DetailCopy title="Label information" value={product.servingInfo} />
          )}
          {product.ingredients && (
            <DetailCopy title="Ingredients" value={product.ingredients} />
          )}
          {product.cautions.length > 0 && (
            <section className="mb-20">
              <h3 className="type-label-sm mb-10">Before you use it</h3>
              <ul className="list-disc pl-20 space-y-8 type-body-sm text-ink-secondary leading-140">
                {product.cautions.map((caution) => (
                  <li key={caution}>{caution}</li>
                ))}
              </ul>
            </section>
          )}
          <p className="type-body-sm text-ink-secondary leading-140 my-20">
            Check the label for allergens and directions. Ask your care team
            about supplements alongside your medications.
          </p>
          {canBuy && (
            <p className="type-body-sm text-ink-secondary leading-140">
              Review your cart and confirm with your saved card. Delivered to
              the shipping address on your account.
            </p>
          )}
        </div>
        <div className="shrink-0 border-t border-line-subtle bg-page p-15 md:p-20 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
          {!canBuy ? (
            <p role="status" className="p-15 bg-raised type-body-md">
              {checkoutEnabled
                ? product.priceStatus === "refreshing"
                  ? "We're checking the price and availability. Please check back shortly."
                  : "This product is currently unavailable. Please check back shortly."
                : "Checkout is not available yet. You can browse the collection."}
            </p>
          ) : (
            <div>
              {cartFull && !inCart && (
                <p className="type-body-sm text-ink-secondary mb-12">
                  You can purchase up to 10 supplement products at a time.
                  Review your cart to make room.
                </p>
              )}
              <div className="flex items-end gap-12">
                <label className="flex flex-col gap-8 w-80 shrink-0">
                  <span className="type-label-xs">Quantity</span>
                  <select
                    aria-label="Quantity"
                    value={quantity}
                    onChange={(event) =>
                      setQuantity(Number(event.target.value))
                    }
                    disabled={inCart}
                    className="w-full h-48 border border-line-default bg-page px-10 text-16 md:text-14 focus-visible:shadow-focus"
                  >
                    {[1, 2, 3].map((count) => (
                      <option key={count} value={count}>
                        {count}
                      </option>
                    ))}
                  </select>
                </label>
                <Button
                  size="lg"
                  onClick={addToCart}
                  className="flex-1 min-w-0 !h-48 !bg-accent"
                >
                  {inCart || cartFull ? "View cart" : "Add to cart"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  );
}

function DetailCopy({ title, value }: { title: string; value: string }) {
  return (
    <section className="mb-20">
      <h3 className="type-label-sm mb-10">{title}</h3>
      <p className="type-body-sm text-ink-secondary leading-140">{value}</p>
    </section>
  );
}
